/**
 * Time utility functions for formatting and calculations
 */

/**
 * Format a time duration in milliseconds to a human-readable string
 * @param {number} milliseconds - Duration in milliseconds
 * @param {Object} options - Formatting options
 * @param {boolean} options.showSeconds - Whether to show seconds (default: true)
 * @param {boolean} options.longFormat - Whether to use long format (e.g., "2 hours 30 minutes" vs "2h 30m") (default: false)
 * @returns {string} Formatted time string
 */
export function formatDuration(milliseconds, options = {}) {
  const { showSeconds = true, longFormat = false } = options;
  
  if (milliseconds <= 0) {
    return longFormat ? 'Disponible ahora' : '0m';
  }
  
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / (1000 * 60)) % 60);
  const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
  
  const parts = [];
  
  if (days > 0) {
    parts.push(longFormat ? `${days} ${days === 1 ? 'día' : 'días'}` : `${days}d`);
  }
  
  if (hours > 0) {
    parts.push(longFormat ? `${hours} ${hours === 1 ? 'hora' : 'horas'}` : `${hours}h`);
  }
  
  if (minutes > 0 || (parts.length === 0 && !showSeconds)) {
    parts.push(longFormat ? `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}` : `${minutes}m`);
  }
  
  if (showSeconds && (seconds > 0 || parts.length === 0)) {
    parts.push(longFormat ? `${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}` : `${seconds}s`);
  }
  
  return parts.join(longFormat ? ' ' : ' ');
}

/**
 * Calculate time until a specific date
 * @param {Date|string} targetDate - Target date (Date object or ISO string)
 * @returns {number} Time in milliseconds until the target date
 */
export function getTimeUntil(targetDate) {
  const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
  const now = new Date();
  
  return Math.max(0, target - now);
}

/**
 * Format time until a specific date
 * @param {Date|string} targetDate - Target date (Date object or ISO string)
 * @param {Object} options - Formatting options (see formatDuration)
 * @returns {string} Formatted time string
 */
export function formatTimeUntil(targetDate, options = {}) {
  if (!targetDate) return options.longFormat ? 'No disponible' : 'N/A';
  
  const timeUntil = getTimeUntil(targetDate);
  return formatDuration(timeUntil, options);
}

/**
 * Get the start of the next day
 * @returns {Date} Date object representing the start of the next day
 */
export function getStartOfNextDay() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
}

/**
 * Get the start of the current day
 * @returns {Date} Date object representing the start of the current day
 */
export function getStartOfDay() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Check if a date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if the date is today
 */
export function isToday(date) {
  const checkDate = date instanceof Date ? date : new Date(date);
  const today = getStartOfDay();
  const tomorrow = getStartOfNextDay();
  
  return checkDate >= today && checkDate < tomorrow;
}

/**
 * Create a countdown timer that updates a state value
 * @param {Date|string} targetDate - Target date to count down to
 * @param {Function} setTimeRemaining - State setter function
 * @param {Object} options - Formatting options (see formatDuration)
 * @returns {Object} Timer control object with start, stop, and isRunning methods
 */
export function createCountdownTimer(targetDate, setTimeRemaining, options = {}) {
  let intervalId = null;
  
  const updateCountdown = () => {
    const timeUntil = getTimeUntil(targetDate);
    
    if (timeUntil <= 0) {
      setTimeRemaining(options.longFormat ? 'Disponible ahora' : '0m');
      stop();
      return;
    }
    
    setTimeRemaining(formatDuration(timeUntil, options));
  };
  
  const start = () => {
    if (intervalId) return;
    
    // Update immediately
    updateCountdown();
    
    // Then update every second
    intervalId = setInterval(updateCountdown, 1000);
  };
  
  const stop = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
  
  return {
    start,
    stop,
    isRunning: () => !!intervalId
  };
}